/**
 * Payment Page - Student payment interface accessed via email link
 * Displays booking details, pricing breakdown, wallet balance option
 * Redirects to Tap payment gateway or confirms if paid by wallet
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSpinner, FaCreditCard, FaWallet, FaClock, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';
import {
  getBookingDetails,
  createPaymentIntent,
  getWalletBalance,
  calculatePricing,
  formatCurrency
} from './paymentService';

const PaymentPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [booking, setBooking] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmount, setWalletAmount] = useState(0);
  const [error, setError] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);

  useEffect(() => {
    loadBookingData();
  }, [bookingId]);

  useEffect(() => {
    if (pricing && walletBalance > 0 && useWallet) {
      // Calculate total after coupon discount (use backend-calculated amount)
      let total = pricing.studentTotal;
      if (appliedCoupon && appliedCoupon.discount_amount) {
        total = Math.max(0, total - appliedCoupon.discount_amount);
      }
      // Calculate how much wallet balance to use
      const maxWalletUse = Math.min(walletBalance, total);
      setWalletAmount(maxWalletUse);
    } else {
      setWalletAmount(0);
    }
  }, [useWallet, walletBalance, pricing, appliedCoupon]);

  const loadBookingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Please sign in to view this page');
        setLoading(false);
        return;
      }

      // Get booking details
      const bookingResult = await getBookingDetails(bookingId);
      if (!bookingResult.success) {
        throw new Error(bookingResult.error);
      }

      const bookingData = bookingResult.booking;

      // Verify student owns this booking
      if (bookingData.student_id !== user.id) {
        setError('You are not authorized to view this booking');
        setLoading(false);
        return;
      }

      // Check if booking is still awaiting payment
      if (bookingData.payment_status !== 'awaiting_payment') {
        if (bookingData.payment_status === 'paid') {
          setError('This booking has already been paid');
        } else if (bookingData.payment_status === 'payment_expired') {
          setError('Payment deadline has expired. This booking has been cancelled.');
        } else {
          setError('This booking is not available for payment');
        }
        setLoading(false);
        return;
      }

      // Calculate time remaining
      const acceptedAt = new Date(bookingData.accepted_at);
      const deadline = new Date(acceptedAt.getTime() + 2 * 60 * 60 * 1000); // 2 hours
      const now = new Date();
      if (now > deadline) {
        setError('Payment deadline has expired. This booking will be cancelled shortly.');
        setLoading(false);
        return;
      }

      setBooking(bookingData);

      // Calculate pricing
      const pricingData = calculatePricing(parseFloat(bookingData.session_price || bookingData.total_price));
      setPricing(pricingData);

      // Get wallet balance
      const walletResult = await getWalletBalance();
      if (walletResult.success) {
        setWalletBalance(walletResult.balance);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading booking:', err);
      setError(err.message || 'Failed to load booking details');
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      setProcessing(true);
      setError(null);

      const sessionPrice = parseFloat(booking.session_price || booking.total_price);

      const result = await createPaymentIntent(
        bookingId,
        sessionPrice,
        useWallet,
        walletAmount
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      // Check if fully paid by wallet
      if (result.fullyPaidByWallet) {
        // Redirect to success page
        navigate(`/payment/success/${bookingId}`);
        return;
      }

      // Redirect to Tap payment gateway
      if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
      } else {
        throw new Error('Payment URL not received');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  const calculateTimeRemaining = () => {
    if (!booking) return '';
    const acceptedAt = new Date(booking.accepted_at);
    const deadline = new Date(acceptedAt.getTime() + 2 * 60 * 60 * 1000); // 2 hours
    const now = new Date();
    const diffMs = deadline - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMinutes}m`;
  };

  const getRemainingAmount = () => {
    if (!pricing) return 0;
    let total = pricing.studentTotal;

    // Apply coupon discount (use backend-calculated amount)
    if (appliedCoupon && appliedCoupon.discount_amount) {
      total = Math.max(0, total - appliedCoupon.discount_amount);
    }

    return Math.max(0, total - walletAmount);
  };

  const getTotalWithCoupon = () => {
    if (!pricing) return 0;
    let total = pricing.studentTotal;

    // Use backend-calculated discount amount
    if (appliedCoupon && appliedCoupon.discount_amount) {
      total = Math.max(0, total - appliedCoupon.discount_amount);
    }

    return total;
  };

  const handleApplyCoupon = async () => {
    setCouponError(null);

    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      const code = couponCode.trim().toUpperCase();

      // Get JWT token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setCouponError('Please sign in to apply coupon');
        return;
      }

      // Call backend API to validate coupon
      const response = await fetch(`${getApiBaseUrl()}/api/booking/validate-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          coupon_code: code,
          booking_total: pricing.studentTotal
        })
      });

      const result = await response.json();

      if (result.valid) {
        // Convert backend response to frontend format
        setAppliedCoupon({
          code: code,
          type: 'fixed', // Backend returns discount_amount directly
          value: result.discount_amount,
          description: result.reward_label || 'Discount applied',
          discount_amount: result.discount_amount,
          new_total: result.new_total
        });
        setCouponError(null);
      } else {
        setCouponError(result.error || 'Invalid coupon code');
        setAppliedCoupon(null);
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      setCouponError('Failed to validate coupon. Please try again.');
      setAppliedCoupon(null);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <FaExclamationTriangle className="text-5xl text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Unavailable</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard/student')}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-800">Complete Payment</h1>
            <div className="flex items-center text-orange-600">
              <FaClock className="mr-2" />
              <span className="font-semibold">{calculateTimeRemaining()} remaining</span>
            </div>
          </div>
          <p className="text-gray-600">
            Your teacher <strong>{booking?.teacher_name}</strong> has accepted your booking request.
            Please complete payment within 2 hours to confirm your session.
          </p>
        </div>

        {/* Session Details */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Session Details</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Teacher:</span>
              <span className="font-semibold">{booking?.teacher_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-semibold">{booking?.date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time:</span>
              <span className="font-semibold">{booking?.time_from} - {booking?.time_to}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-semibold capitalize">{booking?.request_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Students:</span>
              <span className="font-semibold">{booking?.num_students}</span>
            </div>
          </div>
        </div>

        {/* Pricing Breakdown */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Payment Summary</h2>
          <div className="space-y-3 mb-4">
            <div className="flex justify-between text-gray-600">
              <span>Session Price:</span>
              <span>{formatCurrency(pricing?.sessionPrice)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Platform Fee:</span>
              <span>{formatCurrency(pricing?.platformCommission)}</span>
            </div>
            {appliedCoupon && (
              <div className="flex justify-between text-green-600">
                <span>Coupon Discount ({appliedCoupon.code}):</span>
                <span>-{formatCurrency(appliedCoupon.discount_amount)}</span>
              </div>
            )}
            <div className="border-t pt-3 flex justify-between text-lg font-bold text-gray-800">
              <span>Total:</span>
              <span>{formatCurrency(appliedCoupon ? getTotalWithCoupon() : pricing?.studentTotal)}</span>
            </div>
          </div>
        </div>

        {/* Coupon Code Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Have a Coupon Code?</h2>

          {!appliedCoupon ? (
            <div className="space-y-3">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                  placeholder="Enter coupon code"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 uppercase"
                />
                <button
                  onClick={handleApplyCoupon}
                  className="px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
                >
                  Apply
                </button>
              </div>
              {couponError && (
                <p className="text-sm text-red-500">{couponError}</p>
              )}
            </div>
          ) : (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaCheckCircle className="text-green-600 mr-2" />
                  <div>
                    <p className="font-semibold text-gray-800">Coupon Applied: {appliedCoupon.code}</p>
                    <p className="text-sm text-gray-600">{appliedCoupon.description}</p>
                  </div>
                </div>
                <button
                  onClick={handleRemoveCoupon}
                  className="text-red-600 hover:text-red-700 font-semibold text-sm"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Wallet Balance Option */}
        {walletBalance > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl shadow-lg p-6 mb-6 border-2 border-green-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <FaWallet className="text-2xl text-green-600 mr-3" />
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Wallet Balance Available</h3>
                  <p className="text-sm text-gray-600">Current balance: {formatCurrency(walletBalance)}</p>
                </div>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={useWallet}
                  onChange={(e) => setUseWallet(e.target.checked)}
                  className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                />
                <span className="ml-2 font-semibold text-gray-700">Use Wallet</span>
              </label>
            </div>

            {useWallet && (
              <div className="bg-white rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Wallet Amount Used:</span>
                  <span className="font-semibold text-green-600">-{formatCurrency(walletAmount)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-800">
                  <span>Remaining to Pay:</span>
                  <span>{formatCurrency(getRemainingAmount())}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment Button */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <button
            onClick={handlePayment}
            disabled={processing}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
          >
            {processing ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Processing...
              </>
            ) : getRemainingAmount() === 0 ? (
              <>
                <FaCheckCircle className="mr-2" />
                Confirm Payment (Fully Paid by Wallet)
              </>
            ) : (
              <>
                <FaCreditCard className="mr-2" />
                Pay {formatCurrency(getRemainingAmount())}
              </>
            )}
          </button>

          <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
            <span className="mr-2">🔒</span>
            Secure payment powered by Tap Payments
          </div>
        </div>

        {/* Information */}
        <div className="mt-6 bg-blue-50 rounded-xl p-4 text-sm text-gray-600">
          <p className="mb-2">
            <strong>Note:</strong> After successful payment, you will receive a confirmation email with your meeting link.
          </p>
          <p>
            If you don't complete payment within 2 hours, your booking will be automatically cancelled and you'll be notified.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
